/* eslint-disable @typescript-eslint/no-explicit-any */
// Polyfill require for legacy code
import '@js-joda/timezone/dist/js-joda-timezone-1970-2030';
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import 'hammerjs';
import 'js-joda';
import 'pepjs';
import 'ric';

import * as React from 'react';
import * as RollbarModule from 'rollbar';
import { ApolloProvider } from '@apollo/client';
import {
  Payload as LoggerPayload,
  checkIgnore,
  ignoredMessages,
  initDatadogLogging,
  initGlobalLogging,
  logger,
  transformApiExceptionDescription,
  transformForDynamicSourcemap,
} from '@tock/logging';
import { Provider } from 'react-redux';
import { SharedAppConstants } from '@tock/shared-app-constants';
import { StoreType } from '@tock/shared/utils/connect';
import { applyMiddleware, compose, createStore } from 'redux';
import { createApiClient } from 'apiClient';
import { createLocalStorageEnhancer } from '@tock/shared/enhancers/LocalStorage';
import { createRoot } from 'react-dom/client';
import { createTockApiMiddleware } from '@tock/shared/middleware/TockApiMiddleware';
import {
  createTockNativeListener,
  createTockNativeMiddleware,
} from 'middleware/TockNativeMiddleware';
import { getAuthState } from './getAuthState';
import { guardTimezoneChange } from '@tock/shared/utils/timezone';
import { initializeApolloClient } from './initializeApolloClient';
import { initializeNetworkPrinterApi } from 'utils/printer/networkPrinterUtils';
import { initializeStripe } from 'utils/stripeHelpers';
import { isHdModeEnabledSelector } from 'selectors/dashboardPreferenceSelectors';
import { logout } from 'actions/AppActions';
import { resizeCallback } from '@tock/shared/utils/responsive';
import AppConstants from 'constants/AppConstants';
import AppSettings from 'settings/AppSettings';
import Application from './Application';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import defaultBackend from 'store';
import elmMiddleware from 'middleware/elmActions';
import gaLoggerMiddleware from 'middleware/gaLogger';
import localePolyfill from '@tock/shared/utils/locales';
import promiseMiddleware from 'redux-promise';
import realtimePosMiddleware from 'middleware/realtimePos';
import rootReducer from './state';
import smoothscroll from 'smoothscroll-polyfill';
import thunkMiddleware from 'redux-thunk';
import unassignedPartyNextActionMiddleware from 'middleware/unassignedPartyNextActionLogger';

import '../css/index.scss';

import 'polyfills/performance';

if (typeof window.require === 'undefined') {
  window.require = (id: string) => {
    if (id === 'intl' || id.startsWith('intl/')) {
      return window.Intl;
    }
    if (id === 'rollbar') {
      return window.RollbarConstructor;
    }
    if (id.endsWith('.scss') || id.endsWith('.css')) {
      // eslint-disable-next-line no-console
      console.warn(
        `[VITE] require("${id}") for styles called. Please use import instead if possible.`
      );
      return {};
    }
    // eslint-disable-next-line no-console
    console.warn(`[VITE] require("${id}") called but not supported. Returning empty object.`);
    return {};
  };
}

const Rollbar = (RollbarModule as any).default || RollbarModule;
window.RollbarConstructor = Rollbar;

// Safari support for behavior:'smooth' using scrollTo()
smoothscroll.polyfill();

window.React = React;
logger.init(AppSettings.LOG_LEVEL);
const needsPolyfill = !window.Intl;
localePolyfill(needsPolyfill);

const productionBuild = import.meta.env.PROD;
if (productionBuild) {
  // Sample at a rate of 1 / 1000.
  const shouldLogCSPViolations = Math.random() < 0.001;

  const rollbarConfig = {
    accessToken: AppSettings.ROLLBAR_ACCESS_TOKEN,
    captureUncaught: true,
    scrubTelemetryInputs: true,
    autoInstrument: {
      network: false,
      log: true,
      dom: false,
      navigation: false,
      connectivity: true,
      contentSecurityPolicy: shouldLogCSPViolations, // enables telemetry tracking, enabled by default
      errorOnContentSecurityPolicy: shouldLogCSPViolations, // send CSP errors to Rollbar
    },
    captureUnhandledRejections: false,
    checkIgnore: window.location.hostname === 'localhost' ? () => true : checkIgnore, // Ignore all during development
    ignoredMessages,
    payload: {
      environment: AppSettings.ROLLBAR_ENV,
      client: {
        javascript: {
          source_map_enabled: true,

          code_version: window.__BUILD_NUMBER__ || 'dev',
          // Optionally have Rollbar guess which frames the error was thrown from
          // when the browser does not provide line and column numbers.

          guess_uncaught_frames: true,
        },
      },
    },
    transform: (payload: LoggerPayload) => {
      // Side effects!
      transformForDynamicSourcemap(
        payload,
        /^(https?):\/\/dashboard[a-zA-Z0-9._-]*\.exploretock\.com(.*)/,
        'dynamicdashboard'
      );
      transformApiExceptionDescription(payload);
    },
  };
  window.Rollbar = new Rollbar(rollbarConfig);

  //Datadog init
  initDatadogLogging({
    clientToken: AppSettings.DATADOG_CLIENT_TOKEN,
    applicationId: AppSettings.DATADOG_DASHBOARD_APP_ID,
    service: 'tock-dashboard',
    env: AppSettings.DATADOG_ENV,
    version: (window.__BUILD_NUMBER__ || 'dev').toLowerCase(),
    loggingSampleRate: 100,
    rumSampleRate: 100,
    sessionReplaySampleRate: 2,
  });

  // Setup global logging functions
  initGlobalLogging();
}

const logoutFunction = () => {
  if (store) {
    store.dispatch(logout());
  }
  window.location.href = '/logout';
};

const AllConstants = {
  ...AppConstants,
  ...SharedAppConstants,
};

const apiClient = createApiClient({
  apiBase: AppSettings.API_BASE,
  appConstants: AllConstants,
  logoutAction: logoutFunction,
});

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
  ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      actionSanitizer: (action: any) =>
        action.type.startsWith('AUTHENTICATION_')
          ? { ...action, payload: '<<REDACTED_FOR_PERFORMANCE>>' }
          : action,
      stateSanitizer: (state: StoreType) => ({
        ...state,
        app: '<<REDACTED_FOR_PERFORMANCE>>',
      }),
    })
  : compose;

const store = composeEnhancers(
  applyMiddleware(
    createTockApiMiddleware({
      apiClient,
      appConstants: AllConstants,
      getAuthState,
    }),
    createTockNativeMiddleware(),
    unassignedPartyNextActionMiddleware,
    realtimePosMiddleware, // Must come before elm middleware
    gaLoggerMiddleware, // Must come before elm middleware
    elmMiddleware,
    thunkMiddleware,
    promiseMiddleware
  ),
  createLocalStorageEnhancer(
    [
      'announcements',
      'dashboardPreferences',
      'features',
      'flexCalendarSections',
      'mfa',
      'networkPrinters',
      'persistedUi',
      'savedPrinters',
      'selfOnboarding',
      'inventoryScheduleUpdateWorkflows',
      'helpMenu',
    ],
    defaultBackend,
    true
  )
)(createStore)(rootReducer);
createTockNativeListener(store);
initializeNetworkPrinterApi(store);

export type AppDispatch = typeof store.dispatch;

const apolloClient = initializeApolloClient(store);

// Provide the store on the window for modals and easy debugging.
window.store = store;
window.ENABLE_FEATURES_BY_DEFAULT = String(AppSettings.ENABLE_FEATURES_BY_DEFAULT) === 'true';
window.TOUCH_ENABLED = 'ontouchstart' in window;
initializeStripe();

const resizeCallbackWithStore = () => {
  const state = store.getState();
  const { persistedUi } = state;
  const isHdModeEnabled = isHdModeEnabledSelector(state);
  resizeCallback(persistedUi.mobileModeEnabled, isHdModeEnabled);
};

if (!AppSettings.CHECKIN) {
  window.addEventListener('resize', _.throttle(resizeCallbackWithStore));
  const state = store.getState();
  const { persistedUi } = state;
  const isHdModeEnabled = isHdModeEnabledSelector(state);
  resizeCallback(persistedUi.mobileModeEnabled, isHdModeEnabled);
}

function renderInternal() {
  let element = document.getElementById('Root');
  // This is used for tests to render without a Root element.
  if (!element) {
    element = document.createElement('div');
    document.body.appendChild(element);
  }

  if (!window.TOUCH_ENABLED) {
    const body = document.querySelector('body');
    if (body) {
      body.classList.add('no-touch');
    }
  }

  const root = createRoot(element);

  const renderApp = (App: typeof Application) => {
    root.render(
      // <StrictMode>
      <Provider store={store}>
        <ApolloProvider client={apolloClient}>
          <App />
        </ApolloProvider>
      </Provider>
      // </StrictMode>
    );
  };

  try {
    renderApp(Application);
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.error('Failed to render');
    window.LOGGER?.error(e);
    throw e;
  }

  document.body.classList.remove('loading');
}

let counter = 0;
function renderApp() {
  if (window.Intl || window.IntlPolyfillLoaded || ++counter > 20) {
    renderInternal();
    return;
  }
  setTimeout(() => {
    renderApp();
  }, 300);
}

function run() {
  const settingsBootError = (window as any).__TOCK_SETTINGS_BOOT_ERROR__;
  if (settingsBootError) {
    const root = document.getElementById('Root');
    if (root) {
      const cleanSettingsBootError = DOMPurify.sanitize(settingsBootError);
      root.innerHTML = `
        <div style="padding: 24px; text-align: center;">
          <p>Dashboard settings failed to load.</p>
          <p style="font-size: 14px; opacity: 0.8; margin: 20px 0">${cleanSettingsBootError}</p>
          <button type="button" onclick="window.location.reload()" style="border: solid 1px black; padding: 16px;">Click here to reload dashboard</button>
        </div>
      `;
    }
    document.body.classList.remove('loading');
    window.LOGGER?.error('Dashboard settings bootstrap failed', settingsBootError);
    return;
  }

  renderApp();
}

// See if the document loaded. If so kick of fast load.
if (document.getElementById('Root')) {
  run();
} else if (window.addEventListener) {
  // Else kick off a delayed load.
  window.addEventListener('DOMContentLoaded', run);
} else {
  (window as any).attachEvent('onload', run);
}

guardTimezoneChange();
